import { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Pencil, X, Save, AlertTriangle, UserX, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AIInput } from '@/components/ui/ai-input';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn, formatCurrency } from '@/lib/utils';
import type { Supplier } from '@/types';
import { api } from '@/lib/api';
import { ContextMenu } from '@/components/common/ContextMenu';
import { Checkbox } from '@/components/ui/checkbox';

interface SuppliersPageProps {
  onNavigate: (path: string) => void;
}

export function SuppliersPage({ onNavigate }: SuppliersPageProps) {
  const [suppliers, setSuppliers] = useState<(Supplier & { campaigns_count?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [form, setForm] = useState({ name: '', company: '', contact_name: '', contact_email: '', phone: '', notes: '' });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'company'>('name');
  const [showInactive, setShowInactive] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<Supplier | null>(null);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);

  const filteredSuppliers = suppliers
    .filter((supplier) => !search || supplier.name.toLowerCase().includes(search.toLowerCase()) || (supplier.company ?? '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = (sortBy === 'name' ? a.name : (a.company ?? '')).toLowerCase();
      const bv = (sortBy === 'name' ? b.name : (b.company ?? '')).toLowerCase();
      return av.localeCompare(bv);
    });
  const currentYear = new Date().getFullYear();

  const loadSuppliers = useCallback(() => {
    setLoading(true);
    setError(null);
    const q = showInactive ? '?include_inactive=true&limit=100' : '?limit=100';
    api
      .get<{ data?: Supplier[] }>(`/suppliers${q}`)
      .then((res: any) => {
        const data = (res as { data?: Supplier[] }).data;
        if (!Array.isArray(data)) throw new Error('Lieferanten konnten nicht geladen werden.');
        setSuppliers(data.map((s: any) => ({ ...s, campaigns_count: (s as Supplier & { campaigns_count?: number }).campaigns_count ?? 0 })));
      })
      .catch(() => {
        setError('Lieferanten konnten nicht geladen werden.');
        setSuppliers([]);
      })
      .finally(() => setLoading(false));
  }, [showInactive]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const openDeactivateDialog = (s: Supplier) => {
    setDeactivateTarget(s);
  };

  const executeDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivateSubmitting(true);
    try {
      await api.patch(`/suppliers/${deactivateTarget.id}/deactivate`, {});
      setError(null);
      setDeactivateTarget(null);
      loadSuppliers();
    } catch (e) {
      setError((e as Error).message ?? 'Deaktivieren fehlgeschlagen.');
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  const openCreate = () => {
    setEditingSupplier(null);
    setForm({ name: '', company: '', contact_name: '', contact_email: '', phone: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (s: Supplier) => {
    setEditingSupplier(s);
    setForm({
      name: s.name,
      company: s.company ?? '',
      contact_name: s.contact_name ?? '',
      contact_email: s.contact_email ?? '',
      phone: s.phone ?? '',
      notes: s.notes ?? '',
    });
    setDialogOpen(true);
  };

  const saveSupplier = async () => {
    if (saving || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    const payload = { ...form, name: form.name.trim(), contact_email: form.contact_email.trim() || null };
    try {
      if (editingSupplier) {
        await api.patch(`/suppliers/${editingSupplier.id}`, payload);
      } else {
        await api.post('/suppliers', payload);
      }
      setDialogOpen(false);
      loadSuppliers();
    } catch (e) {
      setError((e as Error).message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && suppliers.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Lieferanten</h1>
          <p className="text-sm leading-relaxed text-muted">
            Partner und Zulieferer für Co-Marketing: Stammdaten, Budgets und Verknüpfungen zu Aktivitäten und Kampagnen.
            Über die Detailseite pflegen Sie Ansprechpersonen und KI-Optionen, sofern aktiviert.
          </p>
        </div>
        <Button className="shrink-0" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Lieferant anlegen
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Lieferanten suchen..."
          className="max-w-sm"
        />
        <select
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value as 'name' | 'company')}
          className="rounded-md border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="name">Sortierung: Name</option>
          <option value="company">Sortierung: Firma</option>
        </select>
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-inactive-suppliers"
            checked={showInactive}
            onCheckedChange={(v) => setShowInactive(v === true)}
          />
          <Label htmlFor="show-inactive-suppliers" className="cursor-pointer text-sm font-normal text-muted">
            Inaktive anzeigen
          </Label>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {suppliers.length === 0 ? (
          <Card className="col-span-full flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted" />
            <p className="mt-2 text-muted">Keine Lieferanten vorhanden.</p>
            <Button className="mt-4" onClick={openCreate}>Lieferant anlegen</Button>
          </Card>
        ) : (
          filteredSuppliers.map((s) => {
            const budget = s.budgets?.find((b) => b.year === currentYear);
            const used = budget?.used_budget ?? 0;
            const total = budget?.total_budget ?? 0;
            const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
            return (
              <ContextMenu
                key={s.id}
                actions={[
                  { label: 'Öffnen', onClick: () => onNavigate(`/suppliers/${s.id}`) },
                  ...(s.is_active ? [{ label: 'Bearbeiten', onClick: () => openEdit(s) }] : []),
                  ...(s.is_active
                    ? [{ label: 'Deaktivieren', onClick: () => openDeactivateDialog(s), destructive: true }]
                    : []),
                ]}
              >
                <Card
                  className={cn(
                    'cursor-pointer border-border bg-card shadow-sm transition-shadow hover:shadow'
                  )}
                  onClick={() => onNavigate(`/suppliers/${s.id}`)}
                >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-card-hover text-foreground dark:bg-primary-hover dark:text-muted">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{s.name}</CardTitle>
                        <p className="text-xs text-muted">{s.company || '—'}</p>
                        {!s.is_active && (
                          <Badge variant="secondary" className="mt-1 text-[10px]">Inaktiv</Badge>
                        )}
                      </div>
                    </div>
                    {s.is_active && (
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(s); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted">
                    {s.contact_name && `${s.contact_name} · `}{s.contact_email || '—'}
                  </p>
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-muted">
                      <span>Budget {currentYear}</span>
                      <span>{formatCurrency(used)} / {formatCurrency(total)}</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                    <Badge variant="secondary">{s.campaigns_count ?? 0} aktive Kampagnen</Badge>
                    {s.is_active && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full shrink-0 border-danger/30 text-danger hover:bg-danger-soft sm:w-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeactivateDialog(s);
                        }}
                      >
                        <UserX className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Deaktivieren
                      </Button>
                    )}
                  </div>
                </CardContent>
                </Card>
              </ContextMenu>
            );
          })
        )}
      </div>

      <Dialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deactivateSubmitting) setDeactivateTarget(null);
        }}
      >
        <DialogContent className="max-w-md border-warning/30" showClose={!deactivateSubmitting}>
          <DialogHeader className="space-y-3 text-left">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-soft">
                <AlertTriangle className="h-5 w-5 text-warning" aria-hidden />
              </span>
              <div className="min-w-0 space-y-1">
                <DialogTitle className="text-lg text-warning">Achtung</DialogTitle>
                <DialogDescription className="text-sm text-muted">
                  Lieferant «{deactivateTarget?.name ?? ''}» wirklich deaktivieren? Das lässt sich hier nicht mit einem Klick
                  rückgängig machen.
                </DialogDescription>
              </div>
            </div>
            <div className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2.5 text-sm text-warning dark:text-warning/95">
              <p className="font-medium">Folgen:</p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-muted">
                <li>Alle zugehörigen Aktivitäten und Kampagnen werden inaktiv.</li>
                <li>Jahresbudgets dieses Lieferanten werden inaktiv (keine Budget-Auswertung mehr).</li>
                <li>Verknüpfte Marketingstrategien (optional zugeordnet) werden archiviert.</li>
                <li>KI-Companions dieses Lieferanten werden deaktiviert.</li>
                <li>Der Partner erscheint nicht mehr in Kennzahlen und Standard-Listen.</li>
              </ul>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={deactivateSubmitting}
              onClick={() => setDeactivateTarget(null)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deactivateSubmitting}
              onClick={() => void executeDeactivate()}
              className="gap-2"
            >
              {deactivateSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Wird deaktiviert…
                </>
              ) : (
                <>
                  <UserX className="h-4 w-4" aria-hidden />
                  Ja, deaktivieren
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!saving) setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}</DialogTitle>
          </DialogHeader>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <AIInput id="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="z. B. Media Agentur AG" aiFieldLabel="Lieferantenname" aiContext="Marketing-Lieferant" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company">Firma</Label>
              <AIInput id="company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, company: v }))} placeholder="Firmenname" aiFieldLabel="Firmenname" aiContext={`Lieferant: ${form.name}`} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_name">Ansprechpartner</Label>
              <Input id="contact_name" value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} placeholder="Name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_email">E-Mail</Label>
              <Input id="contact_email" type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} placeholder="email@beispiel.ch" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+41 44 123 45 67" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notizen</Label>
              <AIInput id="notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, notes: v }))} placeholder="Optionale Notizen" aiFieldLabel="Lieferanten-Notizen" aiContext={`Lieferant: ${form.name || form.company}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={saving} onClick={() => setDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button disabled={saving || !form.name.trim()} onClick={saveSupplier}>
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
